import { cn } from '@zealot/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

const detailListVariants = cva('divide-y divide-border text-sm');

const detailRowVariants = cva('flex py-2.5', {
  variants: {
    align: { center: 'items-center', start: 'items-start' },
    layout: { row: 'gap-4', stacked: 'flex-col gap-1.5' },
  },
  defaultVariants: { align: 'start', layout: 'row' },
});

// gloss
const detailRowLabelVariants = cva('text-muted-foreground', {
  variants: {
    layout: {
      row: 'shrink-0 [width:var(--detail-list-label-width,7rem)]',
      stacked: 'w-full',
    },
  },
  defaultVariants: { layout: 'row' },
});

// gloss
type DetailListProps = React.ComponentProps<'dl'> & {
  labelWidth?: string;
};

type DetailRowProps = React.ComponentProps<'div'> &
  VariantProps<typeof detailRowVariants> & {
    label: React.ReactNode;
  };

const DetailList = ({ labelWidth, className, style, children, ...props }: DetailListProps) => (
  <dl
    data-slot="detail-list"
    className={cn(detailListVariants(), className)}
    style={{ ...(labelWidth && { '--detail-list-label-width': labelWidth }), ...style } as React.CSSProperties}
    {...props}
  >
    {children}
  </dl>
);

const DetailRow = ({ label, align, layout, className, children, ...props }: DetailRowProps) => (
  <div data-slot="detail-row" className={cn(detailRowVariants({ align, layout }), className)} {...props}>
    <dt data-slot="detail-row-label" className={cn(detailRowLabelVariants({ layout }))}>
      {label}
    </dt>
    <dd data-slot="detail-row-value" className={cn('min-w-0', layout === 'stacked' ? 'w-full' : 'flex-1')}>
      {children}
    </dd>
  </div>
);

export type { DetailListProps, DetailRowProps };
export { DetailList, DetailRow, detailListVariants, detailRowLabelVariants, detailRowVariants };
